import { CARE_COPY } from "@/design/careCopy";
import { DataUnavailable } from "@/design/DataUnavailable";
import { MealPlanList } from "@/design/MealPlanReader";
import type { PlanListItemView } from "@/design/mealPlanViews";
import { PageShell } from "@/design/ui";
import { AppLink } from "~/components/AppLink";
import { ConsumerPage } from "~/components/ConsumerChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { useQuery } from "~/lib/useQuery";

/**
 * `/care`, My care (H55): docs/ux/screens/ConsumerCareScreen.md.
 *
 * **What the Consumer's clinics prepared for them to read**, one section per
 * kind. Today that is only Meal Plans (H53), and the section appears only once
 * there is one, so somebody whose clinic never writes plans sees one sentence
 * saying what would arrive here rather than an empty "Meal plans" heading.
 *
 * Each section keeps its own context's read and its own rules: this screen
 * collects, it decides nothing (H55).
 */
export function ConsumerCareScreen() {
  const view = useQuery<{ plans: PlanListItemView[] }>("/meal-plans");
  return (
    <ConsumerPage>
      <PageShell>
        <div className="mx-auto w-full max-w-md">
          <h1 className="mb-1 text-xl font-bold text-ink">{CARE_COPY.title}</h1>
          <p className="mb-5 text-sm text-muted">{CARE_COPY.subtitle}</p>
          {view.loading && !view.data ? (
            <ScreenPending />
          ) : view.failure || !view.data ? (
            <DataUnavailable
              configured={view.failure !== "not_configured"}
              what="your care"
            />
          ) : view.data.plans.length === 0 ? (
            <div className="rounded-xl border border-line bg-surface p-5 text-sm shadow-sm">
              <p className="font-semibold text-ink">{CARE_COPY.emptyTitle}</p>
              <p className="mt-1 text-body">{CARE_COPY.emptyDetail}</p>
            </div>
          ) : (
            <section aria-labelledby="care-meal-plans">
              <h2
                id="care-meal-plans"
                className="mb-3 text-sm font-bold uppercase tracking-wide text-muted"
              >
                {CARE_COPY.mealPlans}
              </h2>
              <MealPlanList
                plans={view.data.plans}
                hrefFor={(planId) => `/meal-plans/${planId}`}
                Link={AppLink}
                empty={CARE_COPY.emptyDetail}
              />
            </section>
          )}
        </div>
      </PageShell>
    </ConsumerPage>
  );
}
